import { useCallback, useEffect, useRef, type PointerEvent } from 'react';

/** Movement before a touch counts as a drag (px) – below it, a tap stays a tap. */
export const DRAG_SLOP = 8;
/** A flick this fast (px/ms) decides the direction, however far the finger went. */
const FLICK = 0.4;
/** Resting this long (ms) before release means "put it here", not a flick. */
const HOLD = 120;

export interface DragOptions {
  /** Start of a drag: the current offset (px) the drag continues from. */
  from: () => number;
  /** Every move: the new offset. */
  onMove: (offset: number) => void;
  /** Release: the final offset and the speed (px/ms, positive = in the drag direction). */
  onEnd: (offset: number, velocity: number) => void;
  /** Whether this touch may start a drag (e.g. only on the sheet's header). */
  canStart?: (e: PointerEvent) => boolean;
  /** 'up': moving the finger up grows the offset; 'down': moving it down grows it. */
  direction: 'up' | 'down';
  /** Called when a touch turns into a drag – taps on links below must not fire then. */
  onDragStart?: () => void;
}

/**
 * Follow a finger (or mouse) vertically: the panel moves with it, stays where it is held and is
 * released with its speed, so the caller can snap open or closed (Mike, 14.10.: "beim Wischen
 * fährt es schon mit rauf und runter, und wenn ich es halte, bleibt es stehen"). Moves are read
 * on the window – the finger leaves the bar on the way up. Browsers deliver pointer moves once
 * per frame already, so each one is applied at once (no extra animation frame, which also
 * stalls in a tab that is not drawn).
 */
export function useVerticalDrag(o: DragOptions) {
  const opts = useRef(o);
  useEffect(() => {
    opts.current = o;
  });
  const stop = useRef<(() => void) | null>(null);
  useEffect(() => () => stop.current?.(), []);

  return useCallback((e: PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (opts.current.canStart && !opts.current.canStart(e)) return;
    stop.current?.();
    const sign = opts.current.direction === 'up' ? -1 : 1;
    const startY = e.clientY;
    const base = opts.current.from();
    let dragging = false;
    let offset = base;
    // the last moves, for the speed at release
    let last = { y: startY, t: e.timeStamp };
    let speed = 0;

    const move = (m: globalThis.PointerEvent) => {
      const d = (m.clientY - startY) * sign;
      if (!dragging) {
        if (Math.abs(d) < DRAG_SLOP) return;
        dragging = true;
        opts.current.onDragStart?.();
      }
      const dt = m.timeStamp - last.t;
      if (dt > 0) speed = (((m.clientY - last.y) * sign) / dt) * 0.6 + speed * 0.4;
      last = { y: m.clientY, t: m.timeStamp };
      offset = base + d;
      opts.current.onMove(offset);
    };
    const end = (u?: globalThis.PointerEvent) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      stop.current = null;
      // held still before letting go: no flick, the position decides
      const held = !u || u.timeStamp - last.t > HOLD;
      if (dragging) opts.current.onEnd(offset, held ? 0 : speed);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    stop.current = end;
  }, []);
}

/** Open or closed after a release: a flick wins, otherwise past the middle opens. */
export function snapOpen(offset: number, velocity: number, full: number): boolean {
  if (velocity > FLICK) return true;
  if (velocity < -FLICK) return false;
  return offset > full / 2;
}
